/**
 * Small, same-tab storage primitives for page handoffs.
 *
 * Handoffs carry prepared user input, so they belong in sessionStorage rather
 * than localStorage. Callers choose the key and payload validator; this module
 * only owns the bounded write, safe reads, supersession check, and one-time
 * claim semantics shared by the Quick Chat and One Copilot entry flows.
 */

export type HandoffFailure = 'too-large' | 'storage-full' | 'unavailable'
export type HandoffReplaceResult = HandoffFailure | 'superseded' | null

/**
 * Every major browser gives an origin roughly 5 MB of `sessionStorage`, counted
 * in UTF-16 code units rather than bytes, and shared with everything else the
 * tab has written. A handoff takes a little under half of that.
 *
 * The ceiling is what makes an oversized turn fail fast with an explanation
 * instead of failing at `setItem` with an opaque quota error, and it bounds how
 * long the synchronous serialize can run on the Send interaction. It is sized
 * from the quota rather than picked: a 2 MiB record carries roughly 1.5 MB of
 * attachment content once base64 inflation is accounted for, which covers
 * ordinary screenshots while leaving the rest of the origin's budget alone.
 *
 * Attachments are never shed to fit. A turn the user believes they sent must
 * arrive whole or be refused out loud.
 */
export const DEFAULT_MAX_HANDOFF_PAYLOAD_CHARS = 2 * 1024 * 1024

export type HandoffClaimResult<T> =
  | {status: 'claimed'; value: T}
  | {status: 'empty'}
  | {status: 'unavailable'}
  | {status: 'superseded'}
  | {status: 'invalid'}
  | {status: 'stale'}

export interface HandoffStorage<T> {
  write: (value: T) => HandoffFailure | null
  read: () => T | null
  storedId: () => string | null
  claim: (expectedId?: string) => HandoffClaimResult<T>
  replace: (expectedId: string, value: T) => HandoffReplaceResult
  clear: (expectedId?: string) => boolean
}

interface HandoffStorageOptions<T> {
  key: string
  /** Defaults to {@link DEFAULT_MAX_HANDOFF_PAYLOAD_CHARS}. */
  maxPayloadChars?: number
  /**
   * Cheap lower-bound estimate of the record's cost, summed without copying it.
   * Only used to refuse a payload already past the ceiling before serializing
   * it; the serialized record is measured exactly afterwards.
   */
  payloadChars: (value: T) => number
  validate?: (value: unknown) => value is T
  getId?: (value: T) => string | undefined
  isStale?: (value: T) => boolean
}

function getSessionStorage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage
  } catch {
    // Storage can be unavailable when cookies are blocked or the page is
    // running in a sandboxed frame.
    return null
  }
}

function isQuotaExceeded(error: unknown): boolean {
  if (typeof DOMException === 'undefined' || !(error instanceof DOMException)) return false
  return (
    error.name === 'QuotaExceededError' ||
    error.name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
    error.code === 22 ||
    error.code === 1014
  )
}

function parseValue<T>(raw: string, validate?: (value: unknown) => value is T): T | undefined {
  try {
    const value: unknown = JSON.parse(raw)
    return validate && !validate(value) ? undefined : (value as T)
  } catch {
    return undefined
  }
}

export function createHandoffStorage<T>({
  key,
  maxPayloadChars = DEFAULT_MAX_HANDOFF_PAYLOAD_CHARS,
  payloadChars,
  validate,
  getId,
  isStale,
}: HandoffStorageOptions<T>): HandoffStorage<T> {
  const clear = (expectedId?: string) => {
    const store = getSessionStorage()
    if (!store) return false

    try {
      if (expectedId !== undefined) {
        const raw = store.getItem(key)
        if (!raw || !getId) return false
        const value = parseValue(raw, validate)
        if (!value || getId(value) !== expectedId) return false
      }
      store.removeItem(key)
      return true
    } catch {
      return false
    }
  }

  const read = () => {
    const store = getSessionStorage()
    if (!store) return null

    try {
      const raw = store.getItem(key)
      return raw ? (parseValue(raw, validate) ?? null) : null
    } catch {
      return null
    }
  }

  const storedId = () => {
    const store = getSessionStorage()
    if (!store || !getId) return null

    try {
      const raw = store.getItem(key)
      if (!raw) return null
      return getId(JSON.parse(raw) as T) ?? null
    } catch {
      return null
    }
  }

  const claim = (expectedId?: string): HandoffClaimResult<T> => {
    const store = getSessionStorage()
    if (!store) return {status: 'unavailable'}

    let value: T | undefined
    try {
      const raw = store.getItem(key)
      if (!raw) return {status: 'empty'}
      value = parseValue(raw, validate)
      if (!value) {
        return clear() ? {status: 'invalid'} : {status: 'unavailable'}
      }
    } catch {
      return {status: 'unavailable'}
    }

    if (expectedId !== undefined && getId?.(value) !== expectedId) {
      return {status: 'superseded'}
    }
    if (isStale?.(value)) {
      return clear() ? {status: 'stale'} : {status: 'unavailable'}
    }
    return clear() ? {status: 'claimed', value} : {status: 'unavailable'}
  }

  const write = (value: T): HandoffFailure | null => {
    const store = getSessionStorage()
    if (!store) return 'unavailable'

    // A new command replaces an abandoned command. Refusing to overwrite a
    // record that cannot be removed avoids replaying the wrong command later.
    if (!clear()) return 'unavailable'

    let serialized: string
    try {
      // A payload whose own strings already exceed the ceiling can never fit
      // once serialized, so it is refused without stringifying: a 32 MB document
      // base64-inflates to about 43 MB, and serializing it just to reach the
      // same answer would be a long task on the Send interaction.
      if (payloadChars(value) > maxPayloadChars) return 'too-large'
      serialized = JSON.stringify(value)
    } catch {
      return 'unavailable'
    }

    // The estimate cannot see labels, metadata, or JSON escaping, so the record
    // that will actually be stored is measured before it is offered to the
    // store. Without this a payload just under the estimate could still be
    // refused by the quota, which reads as "storage is full" rather than "this
    // turn is too big".
    if (serialized.length > maxPayloadChars) return 'too-large'

    try {
      store.setItem(key, serialized)
      return null
    } catch (error) {
      return isQuotaExceeded(error) ? 'storage-full' : 'unavailable'
    }
  }

  const replace = (expectedId: string, value: T): HandoffReplaceResult => {
    const store = getSessionStorage()
    if (!store || !getId) return 'unavailable'

    let serialized: string
    try {
      if (payloadChars(value) > maxPayloadChars) return 'too-large'
      serialized = JSON.stringify(value)
    } catch {
      return 'unavailable'
    }
    if (serialized.length > maxPayloadChars) return 'too-large'

    try {
      const raw = store.getItem(key)
      if (!raw) return 'superseded'
      const current = parseValue(raw, validate)
      if (!current || getId(current) !== expectedId) return 'superseded'
      store.setItem(key, serialized)
      return null
    } catch (error) {
      return isQuotaExceeded(error) ? 'storage-full' : 'unavailable'
    }
  }

  return {write, read, storedId, claim, replace, clear}
}
